/**
 * This module provides functions in relation to calling API routes on the backend server and processing the incoming
 * responses. The functions in this module refer to the API endpoint ApiEndPoints.TRANSACTION.
 * @module
 */

import {ApiEndPoints, HttpMethods} from "../base/Settings";
import type {FetchRequest, TransactionInput, Transaction, UUID} from "../base/ui_general/GeneralInterfaces";


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the creation of a new
 * transaction on the server. Only name, description and transaction type need to be specified for this purpose.
 * @param transactionInput An object of type TransactionInput containing the input data for creating the transaction.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_create_transaction(transactionInput: TransactionInput): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.TRANSACTION,
        method: HttpMethods.POST,
        query_parameters: {},
        body: transactionInput,
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for updating an existing
 * transaction on the server.
 * @param transaction An object of type Transaction containing the updated transaction data.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_update_transaction(transaction: Transaction): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.TRANSACTION,
        method: HttpMethods.PUT,
        query_parameters: {},
        body: transaction,
    };
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the query of transactions
 * on the server.
 * @param transaction_id Optional. The UUID of the transaction to query for.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_get_all_transactions(transaction_id?: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.TRANSACTION,
        method: HttpMethods.GET,
        query_parameters: transaction_id ? { transaction_id: transaction_id } : {},
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for deleting an existing
 * transaction on the server.
 * @param transaction_id The UUID of the transaction to delete.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_delete_transaction(transaction_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.TRANSACTION,
        method: HttpMethods.DELETE,
        query_parameters: { transaction_id: transaction_id },
    };
}
